import { useCallback, useEffect, useRef, useState } from 'react';

import {
  useSendMediaMutation,
  useSendWebcamLogMutation,
} from '../certificationApiSlice';

interface UseScreenShareMonitorOptions {
  stream: MediaStream | null;
  enabled: boolean;
  captureIntervalSeconds: number;
  captureQuality: number;
  certificationLogId: string;
  candidateId?: string;
}

interface ScreenShareMonitorState {
  isActive: boolean;
  lastCaptureAt: string | null;
  captureCount: number;
  requestNewStream: () => Promise<MediaStream | null>;
}

function isStreamAlive(stream: MediaStream | null): boolean {
  if (!stream) return false;
  const track = stream.getVideoTracks()[0];
  return Boolean(track && track.readyState === 'live');
}

async function requestFullScreenStream(): Promise<MediaStream | null> {
  if (!navigator.mediaDevices?.getDisplayMedia) return null;

  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'monitor' } as MediaTrackConstraints,
    });

    const track = stream.getVideoTracks()[0];
    if (!track) {
      stream.getTracks().forEach(t => t.stop());
      return null;
    }

    const settings = track.getSettings();
    const displaySurface = (settings as Record<string, unknown>)
      .displaySurface as string | undefined;

    if (displaySurface !== 'monitor') {
      stream.getTracks().forEach(t => t.stop());
      return null;
    }

    return stream;
  } catch {
    return null;
  }
}

function captureFrame(
  video: HTMLVideoElement,
  quality: number,
): Promise<Blob | null> {
  return new Promise(resolve => {
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    if (canvas.width === 0 || canvas.height === 0) {
      resolve(null);
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      resolve(null);
      return;
    }

    ctx.drawImage(video, 0, 0);
    canvas.toBlob(blob => resolve(blob), 'image/jpeg', quality);
  });
}

export function useScreenShareMonitor({
  stream,
  enabled,
  captureIntervalSeconds,
  captureQuality,
  certificationLogId,
  candidateId,
}: UseScreenShareMonitorOptions): ScreenShareMonitorState {
  const [isActive, setIsActive] = useState(() => isStreamAlive(stream));
  const [lastCaptureAt, setLastCaptureAt] = useState<string | null>(null);
  const [captureCount, setCaptureCount] = useState(0);

  const streamRef = useRef<MediaStream | null>(stream);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const captureIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const uploadInProgressRef = useRef(false);

  const [sendMedia] = useSendMediaMutation();
  const [sendWebcamLog] = useSendWebcamLogMutation();

  // Sync external stream changes
  useEffect(() => {
    streamRef.current = stream;
    setIsActive(isStreamAlive(stream));

    if (stream && isStreamAlive(stream)) {
      if (!videoRef.current) {
        videoRef.current = document.createElement('video');
        videoRef.current.playsInline = true;
        videoRef.current.muted = true;
      }
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => undefined);
    }
  }, [stream]);

  // Track ended event
  useEffect(() => {
    if (!stream) return undefined;

    const track = stream.getVideoTracks()[0];
    if (!track) return undefined;

    const handleEnded = () => {
      setIsActive(false);
    };

    track.addEventListener('ended', handleEnded);
    return () => {
      track.removeEventListener('ended', handleEnded);
    };
  }, [stream]);

  // Periodic frame capture
  useEffect(() => {
    if (!enabled || !stream || captureIntervalSeconds <= 0) {
      if (captureIntervalRef.current) {
        clearInterval(captureIntervalRef.current);
        captureIntervalRef.current = undefined;
      }
      return undefined;
    }

    const doCapture = async () => {
      if (uploadInProgressRef.current) return;
      if (!videoRef.current || !isStreamAlive(streamRef.current)) return;

      uploadInProgressRef.current = true;

      try {
        const blob = await captureFrame(videoRef.current, captureQuality);
        if (!blob) return;

        const extra = {
          session: 'screen_capture',
          type: 'image',
          ...(certificationLogId
            ? { certification_log: certificationLogId }
            : {}),
          ...(candidateId ? { player: candidateId } : {}),
        };

        const formData = new FormData();
        formData.append('file', blob, `screen_capture_${Date.now()}.jpg`);
        formData.append('extra', JSON.stringify(extra));

        const uploadResult = await sendMedia(formData).unwrap();
        const uploadedUrl = uploadResult?.uploads?.[0]?.url;

        if (uploadedUrl) {
          const now = new Date();
          await sendWebcamLog({
            time: now.getTime(),
            url: uploadedUrl,
            type: 'screen_capture',
            size: blob.size,
            certification_log: certificationLogId,
            player: candidateId,
          }).unwrap();

          setLastCaptureAt(now.toISOString());
          setCaptureCount(prev => prev + 1);
        }
      } catch {
        // Non-blocking: skip this cycle, retry next
      } finally {
        uploadInProgressRef.current = false;
      }
    };

    captureIntervalRef.current = setInterval(
      doCapture,
      captureIntervalSeconds * 1000,
    );

    return () => {
      if (captureIntervalRef.current) {
        clearInterval(captureIntervalRef.current);
        captureIntervalRef.current = undefined;
      }
    };
  }, [
    candidateId,
    captureIntervalSeconds,
    captureQuality,
    certificationLogId,
    enabled,
    sendMedia,
    sendWebcamLog,
    stream,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (captureIntervalRef.current) {
        clearInterval(captureIntervalRef.current);
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current = null;
      }
    };
  }, []);

  const requestNewStream =
    useCallback(async (): Promise<MediaStream | null> => {
      const oldStream = streamRef.current;
      oldStream?.getTracks().forEach(track => track.stop());

      const newStream = await requestFullScreenStream();
      if (!newStream) {
        setIsActive(false);
        return null;
      }

      streamRef.current = newStream;
      setIsActive(true);

      if (!videoRef.current) {
        videoRef.current = document.createElement('video');
        videoRef.current.playsInline = true;
        videoRef.current.muted = true;
      }
      videoRef.current.srcObject = newStream;
      videoRef.current.play().catch(() => undefined);

      return newStream;
    }, []);

  return {
    isActive,
    lastCaptureAt,
    captureCount,
    requestNewStream,
  };
}
